import React from 'react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'

// Presentational Object that supports local #anchors (within this file) via normal anchor tags, <a href=, and ref= attributes
class Environment extends React.Component {
	componentDidMount() {
		if (window.Prism) {
			window.Prism.highlightAll()
		}
	}

	componentDidUpdate() {
		if (window.Prism) {
			window.Prism.highlightAll()
		}

		let hash = this.props.location.hash.replace('#', '')
		if (hash) {
			let node = this[hash]
			if (node) {
				node.scrollIntoView()
			}
		}
	}

	render() {
		return (
			<div className="javascript-sdk-environment">
				<h1>Environment</h1>
				<p>The VoiceStorm JavaScript SDK creates an environment you can customize.</p>
				<h2
					ref={r => {
						this.global = r
					}}
				>
					VoiceStorm Global Object
				</h2>
				<p>
					The VoiceStorm JavaScript SDK exposes one global JavaScript variable: VoiceStorm. All
					functionality is either exposed as a function or property of VoiceStorm or as a jQuery
					widget. The global VoiceStorm object is not guaranteed to be available until after
					VoiceStormAsyncInit() has been called. This is similar to{' '}
					<a
						href="https://developers.facebook.com/docs/javascript/quickstart"
						target="_blank"
						rel="noopener noreferrer"
					>
						fbAsyncInit in the Facebook JavaScript SDK
					</a>
					.
				</p>
				<p>
					There are several utility functions and objects exposed by the VoiceStorm global object.
					They are:
				</p>
				<div className="data-table-responsive">
					<table className="table table-bordered table-striped bs-table">
						<thead>
							<tr>
								<th style={{ minWidth: '25%' }}>Property Name</th>
								<th>Type</th>
								<th>Description</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>VoiceStorm.jQuery</td>
								<td>Object</td>
								<td>The instance of jQuery used internally by the VoiceStorm SDK</td>
							</tr>
							<tr>
								<td>VoiceStorm.host()</td>
								<td>Function</td>
								<td>Returns the hostname of your Dynamic Signal community</td>
							</tr>
							<tr>
								<td>VoiceStorm.build()</td>
								<td>Function</td>
								<td>Returns the build number of the VoiceStorm SDK</td>
							</tr>
							<tr>
								<td>VoiceStorm.version()</td>
								<td>Function</td>
								<td>
									Returns the version of the Dynamic Signal API the SDK is using (usually
									&ldquo;v1&rdquo;)
								</td>
							</tr>
							<tr>
								<td>VoiceStorm.defaults</td>
								<td>Object</td>
								<td>An object containing overrideable default properties.</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2
					ref={r => {
						this.defaults = r
					}}
				>
					Defaults
				</h2>
				<p>
					There are several default properties you can set to change the behavior of the VoiceStorm
					JavaScript SDK. It is important to only assign properties of the default object,{' '}
					<strong>do not replace or remove the default object itself</strong>!
				</p>
				<div className="data-table-responsive">
					<table
						className="table table-bordered table-striped bs-table"
						style={{ minWidth: '885px' }}
					>
						<thead>
							<tr>
								<th style={{ minWidth: '25%' }}>Property Name</th>
								<th>Type</th>
								<th style={{ whiteSpace: 'nowrap' }}>Default</th>
								<th>Description</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>VoiceStorm.defaults.channelUrl</td>
								<td>string</td>
								<td>&nbsp;</td>
								<td>
									Must be set to support social media authentication (see the{' '}
									<Link to="/javascript-sdk/auth">Social Login</Link> section)
								</td>
							</tr>
							<tr>
								<td>VoiceStorm.defaults.dataPrefix</td>
								<td>string</td>
								<td>'voicestorm'</td>
								<td>Used for data attribute names (see the Widget section)</td>
							</tr>
							<tr>
								<td>VoiceStorm.defaults.shareButtonText</td>
								<td>string</td>
								<td>'Share'</td>
								<td>The text of share buttons created via widgets</td>
							</tr>
							<tr>
								<td>VoiceStorm.defaults.shareButtonClass</td>
								<td>string</td>
								<td>&nbsp;</td>
								<td>The CSS class of share buttons created via widgets</td>
							</tr>
							<tr>
								<td>VoiceStorm.defaults.socialPopupName</td>
								<td>string</td>
								<td>'VoiceStormSocialPopup'</td>
								<td>
									The window.name of popup windows used for social media authentication (see the{' '}
									<Link to="/javascript-sdk/auth">Social Login</Link> section)
								</td>
							</tr>
							<tr>
								<td>VoiceStorm.defaults.submitButtonClass</td>
								<td>string</td>
								<td>&nbsp;</td>
								<td>The CSS class of submit buttons created via widgets</td>
							</tr>
							<tr>
								<td>VoiceStorm.defaults.timeout</td>
								<td>string</td>
								<td>10000</td>
								<td>The timeout in milliseconds of all AJAX calls made by the VoiceStorm SDK</td>
							</tr>
							<tr>
								<td>VoiceStorm.defaults.timezoneOffset</td>
								<td>string</td>
								<td></td>
								<td>
									An{' '}
									<a
										href="http://www.w3.org/TR/NOTE-datetime"
										target="_blank"
										rel="noopener noreferrer"
									>
										ISO 8601
									</a>{' '}
									time zone designator indicating the local timezone set for the community.
									<br />
									<strong>Examples:</strong> '-00:00' indicates Coordinated Universal Time (UTC).
									'-08:00' indicates Pacific Standard Time.
								</td>
							</tr>
							<tr>
								<td>VoiceStorm.defaults.timezoneIanaName</td>
								<td>string</td>
								<td></td>
								<td>
									An{' '}
									<a
										href="http://www.iana.org/time-zones"
										target="_blank"
										rel="noopener noreferrer"
									>
										IANA
									</a>{' '}
									<a
										href="http://en.wikipedia.org/wiki/Tz_database#Names_of_time_zones"
										target="_blank"
										rel="noopener noreferrer"
									>
										time zone name
									</a>{' '}
									indicating the local timezone set for the community. Use this name with 3rd party
									JavaScript libraries like{' '}
									<a href="http://momentjs.com/timezone/" target="_blank" rel="noopener noreferrer">
										Moment Timezone
									</a>{' '}
									to convert times returned by JavaScript APIs (which are always in UTC) to local
									community time.
									<br />
									<strong>Examples:</strong> 'Etc/UTC' indicates Coordinated Universal Time (UTC).
									'America/Los_Angeles' indicates Pacific Standard Time.
								</td>
							</tr>
							<tr>
								<td>VoiceStorm.defaults.reactionProviders</td>
								<td>string array</td>
								<td></td>
								<td>
									A list of social providers that support reactions, like Comment, Share, and Like.
									This list is provided as a convenient way to determine if a particular provider,
									such as Facebook, supports reactions. To get detailed information about all the
									providers available in the community and the reactions they support, call{' '}
									<code>VoiceStorm.api('GET', 'providers')</code>. See{' '}
									<Link to="/api/Registration#get-providers">GET /v1/providers</Link> for more
									details.
									<br />
									<strong>Example:</strong> <code>["Facebook", "Twitter", "YouTube"]</code>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<p>An easy way to set defaults is at the top of your VoiceStormAsyncInit:</p>
				<pre className="language-javascript">
					<code className="language-javascript">{`function VoiceStormAsyncInit()
{
	// Override defaults
	VoiceStorm.defaults.channelUrl = window.location.protocol + '//' + window.location.host + '/channel.html';
	VoiceStorm.defaults.shareButtonText = 'Share Now!';
}`}</code>
				</pre>
			</div>
		)
	}
}

Environment.propTypes = {
	location: PropTypes.object,
}

export default Environment



// WEBPACK FOOTER //
// ./src/components/javascript-sdk/Environment.js